import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Column, Columns, Container } from '@resurs/midas-react'
import { midas } from '@resurs/midas'
import TransactionsUI from './TransactionsUI'
import {
  debitTransactions,
  searchTransactions,
  setCurrentTransaction,
  setCurrentTransactionId,
} from '../../store/actions/transactions-actions'
import { sendTransactionChangeFilterEvent } from '../../util/ga-helpers'
import PageActionBar from '../../internal/PageActionBar'
import { Select } from '../../midasComponents'
import { getText } from '../../internal/TextLabels'
import { getCurrentUserStores } from '../../store/actions/store-info-actions'
import { usePagination } from '../../provider/PaginationProvider'
import { getChannelValues, getStatusValues } from './transactionFilterValues'
import { ALL } from './constants'
import usePaginationEffect from '../../hooks/usePaginationEffect'
import { TransactionSelectFilterTypes } from '../../util/transactionConstants'
import { ChainTypes } from '../../models/chainTypes'
import { Roles } from '../../models/roles'
import { classNames } from '../../util/helpers'

const Transactions = props => {
  const { paginationData, setPaginationData, updateQueryParams } =
    usePagination()
  const hasMountedRef = useRef(false)
  const [loadData, setLoadData] = useState(false)
  const statusValues = getStatusValues()
  const channelValues = getChannelValues()

  const loadTransactions = () => {
    if (paginationData) {
      const newSearchFilters = paginationData?.filters || []
      setLoadData(true)
      props
        .searchTransactions(
          { ...paginationData, sortList: paginationData.sort },
          paginationData.searchQuery,
          newSearchFilters,
        )
        .then(data => {
          setPaginationData({
            ...paginationData,
            ...data.page,
            page: data.page.number,
            results: data.results,
          })
        })
        .finally(() => {
          setLoadData(false)
        })
    }
  }

  const initialPaginationData = {
    page: 0,
    size: 10,
    sort: 'created,desc',
    ...(props.role === Roles.WAREHOUSE
      ? {
          filters: {
            transactionsFilter: TransactionSelectFilterTypes.DEBIT,
          },
        }
      : {}),
  }

  const {
    handleNext,
    handlePrev,
    handleSizeChange,
    handleSearch,
    handleSortChange,
    updatePaginationData,
  } = usePaginationEffect(
    paginationData,
    updateQueryParams,
    loadTransactions,
    hasMounted => {
      if (hasMounted) {
        hasMountedRef.current = hasMounted
      }
    },
    initialPaginationData,
  )

  const updateFilters = newFilters => {
    updatePaginationData(
      'filters',
      {
        ...paginationData.filters,
        ...(newFilters || {}),
      },
      true,
    )
    setLoadData(true)
    sendTransactionChangeFilterEvent(paginationData.filters)
  }

  const filterTransactionsList = theFilter => {
    updateFilters({ transactionsFilter: theFilter })
  }

  const filterTransactionsListByChannel = theFilter => {
    updateFilters({ channelFilter: theFilter })
  }

  const filterTransactionsListByStore = theFilter => {
    updateFilters({ selectedStoreId: theFilter })
  }

  const doDebitTransactions = (invoices, otherTransactions) => {
    const transactionsToDebit = invoices.concat(otherTransactions)
    if (props.hasInvoiceNumber) {
      props.debitTransactions(transactionsToDebit, true)
    } else {
      props.debitTransactions(transactionsToDebit, false)
    }
  }

  const handleClick = transaction => {
    if (transaction.id) {
      props.setCurrentTransactionId(transaction.id)
    } else {
      props.setCurrentTransaction(transaction)
    }

    props.nextRoute('transaction')
  }

  useEffect(() => {
    props.getCurrentUserStores()
  }, [])

  const sortValues = values =>
    values
      .slice()
      .sort(
        (a, b) =>
          (a.key === 'all' && -1) ||
          (b.key === 'all' && 1) ||
          a.valueLabel.toUpperCase().localeCompare(b.valueLabel.toUpperCase()),
      )

  const renderSingleFilter = (
    <PageActionBar
      items={
        <div
          style={{
            display: 'flex',
            marginTop: '1rem',
          }}
        >
          <Select
            onChange={filterTransactionsList}
            id="filter-transactions"
            value={paginationData?.filters['transactionsFilter']}
            values={sortValues(statusValues)}
          />
          <span style={{ padding: '0.4rem' }} />
          <Select
            onChange={filterTransactionsListByChannel}
            id="filter-transactions-channel"
            value={paginationData?.filters['channelFilter']}
            values={sortValues(channelValues)}
          />
          {props.currentStore?.chainType === ChainTypes.SINGLE_OWNER && (
            <>
              <span style={{ padding: '0.4rem' }} />
              <Select
                onChange={filterTransactionsListByStore}
                id="filter-transactions-store"
                value={paginationData?.filters['selectedStoreId']}
                values={[
                  {
                    key: ALL,
                    value: ALL,
                    valueLabel: getText('transactions.filter.showAllStores'),
                  },
                  ...props.stores
                    .map(({ id, tradeName, accountSystemStoreId }) => ({
                      key: id,
                      value: id,
                      valueLabel: `${tradeName} (${accountSystemStoreId})`,
                    }))
                    .sort((a, b) => a.valueLabel.localeCompare(b.valueLabel)),
                ]}
              />
            </>
          )}
        </div>
      }
      actions={
        <button
          type="button"
          title={getText('transactions.resetTitle')}
          onClick={() => updateQueryParams(initialPaginationData)}
          className={classNames(midas.button, midas.buttonPrimary)}
        >
          {getText('transactions.reset')}
        </button>
      }
    />
  )

  return (
    <Container>
      <Columns>
        <Column col12>
          {paginationData ? (
            <TransactionsUI
              debit={doDebitTransactions}
              pagination={{
                pageNumber: paginationData.page,
                totalPages: paginationData.totalPages,
                size: paginationData.size,
                totalElements: paginationData.totalPages,
                sortList: paginationData.sort,
              }}
              onLoadSearchData={handleSearch}
              onClick={id => {
                handleClick(id)
              }}
              filtering={renderSingleFilter}
              searchTerm={paginationData?.searchQuery}
              role={props.role}
              debitLoading={props.debitLoading}
              loadTransactions={loadTransactions}
              hasInvoiceNumber={props.hasInvoiceNumber}
              debitDisabled={props.debitDisabled}
              onNext={handleNext}
              onPrev={handlePrev}
              onChangeSize={handleSizeChange}
              onChangeSortPriority={handleSortChange}
              isLoading={loadData}
            />
          ) : (
            <div
              className={midas.container}
              style={{
                width: '100%',
                height: '100%',
                zIndex: 10001,
                position: 'fixed',
                top: '25%',
                left: '50%',
              }}
            >
              <span className={midas.loading} />
            </div>
          )}
        </Column>
      </Columns>
    </Container>
  )
}

Transactions.propTypes = {
  searchTransactions: PropTypes.func.isRequired,
  setCurrentTransaction: PropTypes.func.isRequired,
  setCurrentTransactionId: PropTypes.func.isRequired,
  debitTransactions: PropTypes.func.isRequired,
  nextRoute: PropTypes.func.isRequired,
  role: PropTypes.string.isRequired,
  hasInvoiceNumber: PropTypes.bool.isRequired,
  debitLoading: PropTypes.bool.isRequired,
  stores: PropTypes.array.isRequired,
  getCurrentUserStores: PropTypes.func.isRequired,
}

const mapStateToProps = state => {
  return {
    role: state.auth.role,
    hasInvoiceNumber: state.auth.hasInvoiceNumber,
    debitLoading: state.transactions.debitLoading,
    stores: state.storeInfo.stores,
    currentStore: state.storeInfo.currentStore,
  }
}

const mapDispatchToProps = dispatch => ({
  searchTransactions: (pageInfo, searchTerm, filters) =>
    dispatch(searchTransactions(pageInfo, searchTerm, filters)),
  setCurrentTransactionId: id => {
    dispatch(setCurrentTransactionId(id))
  },
  setCurrentTransaction: transaction => {
    dispatch(setCurrentTransaction(transaction))
  },
  debitTransactions: (transactions, hasInvoiceNumber) => {
    dispatch(debitTransactions(transactions, hasInvoiceNumber))
  },
  getCurrentUserStores: () => {
    dispatch(getCurrentUserStores())
  },
})

export default connect(mapStateToProps, mapDispatchToProps)(Transactions)
